import type { LayerRect } from './types';

/** Minimum fractional width/height for a layer box, so a resize can never
 *  collapse it to zero area. There is intentionally no maximum — layers scale
 *  up freely — and no position clamp, so a layer may sit fully offscreen. */
export const MIN_LAYER_SIZE = 0.05;

/** Move the whole box by a fractional delta. No clamping: the origin may go
 *  negative or past 1, leaving the layer partially or fully offscreen. */
export function applyCanvasMove(
  { rect, dx, dy }: { rect: LayerRect; dx: number; dy: number },
): LayerRect {
  return { x: rect.x + dx, y: rect.y + dy, w: rect.w, h: rect.h };
}

/** Resize from a corner by a fractional delta. Width/height are floored at
 *  MIN_LAYER_SIZE (no upper bound). When `lockAspect` is set (image/video), the
 *  media aspect is preserved and the anchored edges are recomputed so the box
 *  keeps hugging the media. Mirrors the geometry previously inlined in
 *  LayerCanvasEditor. */
export function applyCanvasResize(
  { rect, corner, dx, dy, lockAspect }: {
    rect: LayerRect;
    corner: 'nw' | 'ne' | 'sw' | 'se';
    dx: number;
    dy: number;
    lockAspect: boolean;
  },
): LayerRect {
  let { x, y, w, h } = rect;

  if (corner === 'se') {
    w = Math.max(MIN_LAYER_SIZE, rect.w + dx);
    h = Math.max(MIN_LAYER_SIZE, rect.h + dy);
  } else if (corner === 'ne') {
    w = Math.max(MIN_LAYER_SIZE, rect.w + dx);
    h = Math.max(MIN_LAYER_SIZE, rect.h - dy);
    y = rect.y + dy;
  } else if (corner === 'sw') {
    w = Math.max(MIN_LAYER_SIZE, rect.w - dx);
    h = Math.max(MIN_LAYER_SIZE, rect.h + dy);
    x = rect.x + dx;
  } else if (corner === 'nw') {
    w = Math.max(MIN_LAYER_SIZE, rect.w - dx);
    h = Math.max(MIN_LAYER_SIZE, rect.h - dy);
    x = rect.x + dx;
    y = rect.y + dy;
  }

  if (lockAspect && rect.h > 0) {
    // Width drives; height follows the original rect's aspect. Re-anchor the
    // edges that moved for the dragged corner so the opposite corner stays put.
    const ratio = rect.w / rect.h;
    h = Math.max(MIN_LAYER_SIZE, w / ratio);
    w = h * ratio;
    if (corner === 'ne' || corner === 'nw') y = rect.y + rect.h - h;
    if (corner === 'sw' || corner === 'nw') x = rect.x + rect.w - w;
    if (corner === 'se' || corner === 'ne') x = rect.x;
    if (corner === 'se' || corner === 'sw') y = rect.y;
  }

  return { x, y, w, h };
}
